import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
  type InfiniteData,
} from "@tanstack/react-query";
import { IconBell, IconBellOff, IconPointFilled, IconLoader2 } from "@tabler/icons-react";
import { useNotificationsStore } from "../stores/notificationsStore";
import {
  listNotifications,
  markNotificationRead,
  markAllNotificationsRead,
  type NotificationPage,
} from "../api";
import {
  NOTIFICATION_TYPES,
  NOTIFICATION_TYPE_LABEL,
  type NotificationType,
  type StoredNotification,
} from "../types";
import {
  notificationRoute,
  notificationSubtitle,
  notificationTitle,
} from "../components/notification-formatters";
import {
  notificationIcon,
  notificationIconTint,
} from "../components/notification-icons";
import { formatRelative } from "@/lib/format/date";
import { cn } from "@/lib/utils/cn";

const HISTORY_KEY = ["agency-notifications-history"];

export function NotificationsPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // Tempo real (sessão): eventos chegando pelo stream SSE.
  const live = useNotificationsStore((s) => s.notifications);
  const storeMarkAsRead = useNotificationsStore((s) => s.markAsRead);
  const storeMarkAllAsRead = useNotificationsStore((s) => s.markAllAsRead);
  const status = useNotificationsStore((s) => s.connectionStatus);

  const [type, setType] = useState<NotificationType | "">("");
  const [onlyUnread, setOnlyUnread] = useState(false);

  // Histórico persistido (cursor/keyset) — base da lista; o SSE prepende ao vivo.
  const { data, isLoading, hasNextPage, isFetchingNextPage, fetchNextPage } =
    useInfiniteQuery({
      queryKey: HISTORY_KEY,
      queryFn: ({ pageParam }) => listNotifications(pageParam, 20),
      initialPageParam: null as string | null,
      getNextPageParam: (last) => last.nextCursor ?? undefined,
    });

  const setReadInCache = (predicate: (n: StoredNotification) => boolean) =>
    queryClient.setQueryData<InfiniteData<NotificationPage>>(HISTORY_KEY, (old) =>
      old
        ? {
            ...old,
            pages: old.pages.map((p) => ({
              ...p,
              items: p.items.map((n) =>
                predicate(n) ? { ...n, read: true } : n,
              ),
            })),
          }
        : old,
    );

  const markRead = useMutation({
    mutationFn: (id: string) => markNotificationRead(id),
    onMutate: (id) => {
      storeMarkAsRead(id);
      setReadInCache((n) => n.id === id);
    },
  });

  const markAll = useMutation({
    mutationFn: markAllNotificationsRead,
    onMutate: () => {
      storeMarkAllAsRead();
      setReadInCache(() => true);
    },
  });

  // Mescla tempo real + histórico, deduplicando por id (live no topo).
  const history = data?.pages.flatMap((p) => p.items) ?? [];
  const seen = new Set<string>();
  const merged: StoredNotification[] = [];
  for (const n of [...live, ...history]) {
    if (!seen.has(n.id)) {
      seen.add(n.id);
      merged.push(n);
    }
  }

  const rows = merged
    .filter((n) => !type || n.type === type)
    .filter((n) => !onlyUnread || !n.read);

  const unread = merged.filter((n) => !n.read).length;
  const connected = status === "connected";

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="flex flex-col gap-1">
          <h2 className="flex items-center gap-2 text-3xl font-black tracking-tight text-ink">
            <IconBell size={28} className="text-brand-600" />
            Notificações
          </h2>
          <p className="text-ink-soft font-medium">
            Equipe, licenças, seats do plano e suporte — em tempo real.
          </p>
        </div>
        {unread > 0 && (
          <button
            onClick={() => markAll.mutate()}
            className="inline-flex items-center justify-center gap-2 rounded-2xl border border-border bg-white px-5 py-2.5 text-sm font-black uppercase tracking-wider text-ink-muted transition-all hover:border-brand-300 hover:text-brand-600"
          >
            Marcar todas como lidas
          </button>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <select
          value={type}
          onChange={(e) => setType(e.target.value as NotificationType | "")}
          className="rounded-xl border border-border bg-white px-3 py-2 text-sm outline-none transition-all focus:border-brand-500 focus:ring-4 focus:ring-brand-500/10"
        >
          <option value="">Todos os tipos</option>
          {NOTIFICATION_TYPES.map((k) => (
            <option key={k} value={k}>
              {NOTIFICATION_TYPE_LABEL[k]}
            </option>
          ))}
        </select>
        <label className="flex items-center gap-1.5 text-sm text-ink-muted">
          <input
            type="checkbox"
            checked={onlyUnread}
            onChange={(e) => setOnlyUnread(e.target.checked)}
            className="h-4 w-4 accent-brand-600"
          />
          Só não lidas
        </label>
        <span
          className={cn(
            "ml-auto inline-flex items-center gap-1.5 text-[12px] font-bold",
            connected ? "text-success" : "text-ink-soft",
          )}
        >
          <IconPointFilled
            size={14}
            className={connected ? "text-success" : "text-ink-soft"}
          />
          {connected ? "Conectado (tempo real)" : "Desconectado"}
        </span>
      </div>

      <div className="rounded-3xl border border-white/40 bg-white/70 shadow-card backdrop-blur-sm overflow-hidden">
        {isLoading ? (
          <div className="py-16 text-center text-ink-soft font-bold italic animate-pulse">
            Carregando notificações...
          </div>
        ) : rows.length === 0 ? (
          <div className="flex flex-col items-center gap-3 py-16 text-center">
            <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-bg text-ink-soft">
              <IconBellOff size={22} />
            </span>
            <p className="text-lg font-bold text-ink">Nada por aqui</p>
            <p className="text-sm text-ink-muted">
              Nenhuma notificação para o filtro atual.
            </p>
          </div>
        ) : (
          <>
            <ul className="divide-y divide-border-soft">
              {rows.map((n) => (
                <li key={n.id}>
                  <button
                    type="button"
                    onClick={() => {
                      if (!n.read) markRead.mutate(n.id);
                      navigate(notificationRoute(n));
                    }}
                    className={cn(
                      "flex w-full items-start gap-4 px-6 py-4 text-left transition-colors hover:bg-white/50",
                      !n.read && "bg-brand-50/40",
                    )}
                  >
                    <span
                      className={cn(
                        "mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl shadow-inner-light",
                        notificationIconTint[n.type],
                      )}
                    >
                      {notificationIcon(n.type, 18)}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] font-black uppercase tracking-wider text-brand-400">
                          {NOTIFICATION_TYPE_LABEL[n.type]}
                        </span>
                        {!n.read && (
                          <span className="h-2 w-2 rounded-full bg-brand-500" />
                        )}
                      </div>
                      <p
                        className={cn(
                          "mt-0.5 text-sm",
                          n.read ? "text-ink-muted" : "font-bold text-ink",
                        )}
                      >
                        {notificationTitle(n)}
                      </p>
                      {notificationSubtitle(n) && (
                        <p className="truncate text-xs text-ink-soft">
                          {notificationSubtitle(n)}
                        </p>
                      )}
                    </div>
                    <span className="shrink-0 text-[11px] font-medium text-ink-soft">
                      {formatRelative(n.receivedAt)}
                    </span>
                  </button>
                </li>
              ))}
            </ul>

            {hasNextPage && (
              <div className="border-t border-border-soft p-4 text-center">
                <button
                  onClick={() => fetchNextPage()}
                  disabled={isFetchingNextPage}
                  className="inline-flex items-center gap-2 rounded-xl border border-border bg-white px-6 py-2.5 text-sm font-black uppercase tracking-wider text-ink-muted transition-all hover:border-brand-300 hover:text-brand-600 disabled:opacity-50"
                >
                  {isFetchingNextPage && (
                    <IconLoader2 size={16} className="animate-spin" />
                  )}
                  Carregar mais
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
